import {
  BufferAttribute,
  BufferGeometry,
  Color,
  Group,
  LineBasicMaterial,
  LineSegments,
  Object3D,
} from "three";
import { worldMatrixFromOpenCV } from "../data/coords";
import type { SceneCamera } from "../data/types";

/**
 * A small wireframe widget visualising one calibrated camera's pose and FOV.
 *
 * Built from K, R_wc, t_wc, width, height. Internally creates a Group whose
 * world matrix is set via the OpenCV→three.js conversion in coords.ts, then
 * attaches LineSegments tracing the camera's frustum (apex at origin in the
 * local frame, frustum extending along -Z in three.js convention).
 */
export interface CameraRig {
  id: string;
  group: Group;
  setHighlighted(on: boolean): void;
}

const NEAR = 0.02;     // metres
const FAR = 0.4;       // metres — small enough to read as a "camera widget"
                       // rather than dominate the scene with overlapping cones.

export function buildCameraRigs(cams: SceneCamera[]): CameraRig[] {
  return cams.map(buildOne);
}

function buildOne(cam: SceneCamera): CameraRig {
  const group = new Group();
  group.name = cam.id;
  group.matrixAutoUpdate = false;
  group.matrix.copy(worldMatrixFromOpenCV(cam));
  group.matrixWorldNeedsUpdate = true;

  group.add(makeFrustum(cam, 0x66ccff));

  return {
    id: cam.id,
    group,
    setHighlighted(on: boolean) {
      const seg = group.children[0] as LineSegments;
      (seg.material as LineBasicMaterial).color.set(on ? 0xffaa33 : 0x66ccff);
    },
  };
}

/**
 * Frustum wireframe: 8 edges around a truncated pyramid (apex at origin,
 * extending along -Z), plus 4 edges connecting near & far rectangles.
 */
function makeFrustum(cam: SceneCamera, color: number): Object3D {
  const fovY = (cam.fov_y_deg * Math.PI) / 180;
  const aspect = cam.aspect;

  const halfHNear = Math.tan(fovY / 2) * NEAR;
  const halfWNear = halfHNear * aspect;
  const halfHFar = Math.tan(fovY / 2) * FAR;
  const halfWFar = halfHFar * aspect;

  // Frustum corners in three.js camera local space: -Z forward, +Y up, +X right.
  const n = [
    [-halfWNear, -halfHNear, -NEAR],
    [ halfWNear, -halfHNear, -NEAR],
    [ halfWNear,  halfHNear, -NEAR],
    [-halfWNear,  halfHNear, -NEAR],
  ];
  const f = [
    [-halfWFar, -halfHFar, -FAR],
    [ halfWFar, -halfHFar, -FAR],
    [ halfWFar,  halfHFar, -FAR],
    [-halfWFar,  halfHFar, -FAR],
  ];

  // 4 near edges + 4 far edges + 4 side edges = 12 segments × 2 verts × 3 floats.
  const positions = new Float32Array(12 * 2 * 3);
  let p = 0;
  const push = (a: number[], b: number[]) => {
    positions[p++] = a[0]; positions[p++] = a[1]; positions[p++] = a[2];
    positions[p++] = b[0]; positions[p++] = b[1]; positions[p++] = b[2];
  };
  // near rect
  push(n[0], n[1]); push(n[1], n[2]); push(n[2], n[3]); push(n[3], n[0]);
  // far rect
  push(f[0], f[1]); push(f[1], f[2]); push(f[2], f[3]); push(f[3], f[0]);
  // sides
  push(n[0], f[0]); push(n[1], f[1]); push(n[2], f[2]); push(n[3], f[3]);

  const geom = new BufferGeometry();
  geom.setAttribute("position", new BufferAttribute(positions, 3));
  const mat = new LineBasicMaterial({ color: new Color(color) });
  return new LineSegments(geom, mat);
}
